import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  reqValGreterThanNumber0,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import {
  createEmailAutoTrigger,
  getEmailAutoTriggerByID,
  updateEmailAutoTrigger,
} from "./services/emailAutoTrigger.services";
import { clearEmailAutoTriggerDetails } from "./emailAutoTriggerSlice";
import {
  TRIGGERMODULE,
  TRIGGEROPERATION,
  TRIGGERWATO,
} from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { Label } from "@progress/kendo-react-labels";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateEmailAutoTrigger: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const EmailAutoTriggerID = location.state?.EmailAutoTriggerID;
  const loading = useAppSelector((state) => state.emailAutoTrigger.loading);
  const EmailAutoTriggerDetail = useAppSelector(
    (state) => state.emailAutoTrigger.EmailAutoTriggerDetail
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearEmailAutoTriggerDetails());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [EmailAutoTriggerDetail]);

  useEffect(() => {
    if (EmailAutoTriggerID) {
      dispatch(getEmailAutoTriggerByID(EmailAutoTriggerID));
    }
  }, [EmailAutoTriggerID]);

  const handleSubmit = async (values: any) => {
    try {
      if (EmailAutoTriggerID) {
        const editPayload = {
          ID: EmailAutoTriggerID,
          TriggerName: values?.TriggerName ? values?.TriggerName : "",
          Module: values?.Module ? values?.Module : "",
          Operation: values?.Operation ? values?.Operation : "",
          Email_To: values?.Email_To ? values?.Email_To : "",
          DelayDay: values?.DelayDay ? +values?.DelayDay : null,
          DelayHour: values?.DelayHour ? +values?.DelayHour : null,
          DelayTime: values?.DelayTime ? +values?.DelayTime : null,
          SubjectName: values?.SubjectName ? values?.SubjectName : "",
          EmailText: values?.EmailText ? values?.EmailText : "",
        };
        const response = await dispatch(updateEmailAutoTrigger(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          TriggerName: values?.TriggerName ? values?.TriggerName : "",
          Module: values?.Module ? values?.Module : "",
          Operation: values?.Operation ? values?.Operation : "",
          Email_To: values?.Email_To ? values?.Email_To : "",
          DelayDay: values?.DelayDay ? +values?.DelayDay : null,
          DelayHour: values?.DelayHour ? +values?.DelayHour : null,
          DelayTime: values?.DelayTime ? +values?.DelayTime : null,
          SubjectName: values?.SubjectName ? values?.SubjectName : "",
          EmailText: values?.EmailText ? values?.EmailText : "",
        };
        const response = await dispatch(createEmailAutoTrigger(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/autotrigger/email");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={EmailAutoTriggerDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {EmailAutoTriggerID
                        ? "Update Email Automation Trigger"
                        : "Add Email Automation Trigger"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TriggerName"
                      label="Trigger Name"
                      placeholder="Trigger Name"
                      component={FormTextField}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"Module"}
                      name={"Module"}
                      label={"Module"}
                      placeholder="i.e. Lead"
                      astrike={true}
                      validator={requiredValidator}
                      component={FormSelectionField}
                      options={TRIGGERMODULE?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"Operation"}
                      name={"Operation"}
                      label={"Operation"}
                      placeholder="i.e. Inserted"
                      astrike={true}
                      validator={requiredValidator}
                      component={FormSelectionField}
                      options={TRIGGEROPERATION?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"Email_To"}
                      name={"Email_To"}
                      label={"Email To"}
                      placeholder="i.e. Lead"
                      astrike={true}
                      validator={requiredValidator}
                      component={FormSelectionField}
                      options={TRIGGERWATO?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Label style={{ marginTop: 14 }}>Delay By Day & Time</Label>
                    <div className="d-flex gap-2">
                      <Field
                        wrapperClassName="w-100 m-0"
                        name="DelayDay"
                        label=""
                        placeholder="Day"
                        min={0}
                        format="n0"
                        component={FormNumericTextField}
                        validator={reqValGreterThanNumber0}
                      />
                      <Field
                        wrapperClassName="w-100 m-0"
                        fieldClassName="no-spinner"
                        name="DelayHour"
                        label=""
                        placeholder="HH"
                        type="number"
                        minNumber="0"
                        maxNumber="23"
                        component={FormTextField}
                        validator={requiredValidator}
                      />
                      <span style={{ marginTop: 5 }}>:</span>
                      <Field
                        wrapperClassName="w-100 m-0"
                        fieldClassName="no-spinner"
                        name="DelayTime"
                        label=""
                        placeholder="MM"
                        type="number"
                        minNumber="0"
                        maxNumber="59"
                        component={FormTextField}
                        validator={requiredValidator}
                      />
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem></GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="SubjectName"
                      label="Subject Name"
                      placeholder="Subject Name"
                      component={FormTextField}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="EmailText"
                      label="Email Text"
                      placeholder="Email Text"
                      component={FormRichTextField}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={EmailAutoTriggerID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/autotrigger/email")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateEmailAutoTrigger;
